import { useCallback, useRef, useState } from "react";

/**
 * Which record an edit popup is showing, and which one it asked for.
 *
 * The edit popups stay mounted between opens. Without this, a failed load
 * (an expired session, a dropped request) or a slow reply overtaken by the
 * next open left the PREVIOUS record in the form — Navya opened Chamath
 * Palihapitiya's recommender and got Aniela Gregorek's, and Update would have
 * saved Aniela onto Chamath (25 Oct 2026). With it, a popup:
 *
 *   load.begin(id)                 when it opens (every open, not only a new id)
 *   load.accepts(id, record)       before applying a reply — drops a reply for
 *                                  an earlier open, or for a different record
 *   load.fail(id, error)           when the load fails
 *   load.loaded(record)            once the reply is in the form
 *   load.reset()                   when it closes
 *   load.isLoadedFor(id)           gates the form and the Update button
 *
 * The recommender popup (`pages/recommend/editRecommendByPopup.js`) does the
 * same inline.
 */
export default function useEditLoad() {
  const requestedRef = useRef(null);
  const [loadedId, setLoadedId] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const begin = useCallback((id) => {
    requestedRef.current = id ? String(id) : null;
    setLoadedId(null);
    setLoadError(null);
  }, []);

  const isCurrent = useCallback((id) => requestedRef.current !== null && requestedRef.current === String(id), []);

  const accepts = useCallback(
    (id, record) => isCurrent(id) && Boolean(record) && (!record._id || String(record._id) === String(id)),
    [isCurrent]
  );

  const loaded = useCallback((record) => {
    if (requestedRef.current) setLoadedId(String(record?._id || requestedRef.current));
  }, []);

  const fail = useCallback(
    (id, error) => {
      if (!isCurrent(id)) return;
      const message =
        typeof error === "string" ? error : error?.response?.data?.message || error?.response?.data?.error || "";
      setLoadError(`This record could not be loaded${message ? ` (${message})` : ""}. Close and try again.`);
    },
    [isCurrent]
  );

  const reset = useCallback(() => {
    requestedRef.current = null;
    setLoadedId(null);
    setLoadError(null);
  }, []);

  const isLoadedFor = useCallback((id) => Boolean(id) && loadedId === String(id), [loadedId]);

  return { begin, accepts, loaded, fail, reset, isLoadedFor, loadError };
}
